import { Injectable } from '@angular/core';
import { OeAppService } from './oe-app.service';
import { ProfileServiceConfigurationService } from './profile-service-configuration.service';
import { PersonDataDto } from './types/PersonDataDto';
import { BilingualFallbackType } from './types/bilingual-fallback-type';
import { Language } from './types/language';
import { LevelingAction } from './types/leveling-action';
import { TeacherType } from './types/teacher-type';
import { Program } from './types/program-type';
import { CohortHelpers } from '../home/cohort/cohort.helpers';

@Injectable()
export class OeAppPersonService {
  private _showOnboardingModalOnGroupClass: boolean;

  constructor(
    private readonly oeApp: OeAppService,
    private readonly profileServiceConfigurationService: ProfileServiceConfigurationService
  ) {
    profileServiceConfigurationService.isPlatformTourGCEnabled$().subscribe((isEnabled) => {
      this._showOnboardingModalOnGroupClass = isEnabled && this.enforcePlatformTour;
    });
  }

  getPersonData(): PersonDataDto {
    return this.oeApp.getPersonData();
  }

  isB2bStudent(): boolean {
    return this.getPersonData().b2b;
  }

  isPcUpsellEligible(): boolean {
    return this.getPersonData().pcUpsellEligible;
  }

  get pcUpsellEligible(): boolean {
    return this.getPersonData().pcUpsellEligible;
  }

  hasLiveClassAccess(): boolean {
    return this.getPersonData().hasLiveClassAccess;
  }

  get levelNum(): number {
    return +this.getPersonData().levelNum;
  }

  getShowOnboardingBanner() {
    return this.getPersonData().showPlatformTourBanner;
  }

  showOnboardingModalOnGroupClass() {
    return this._showOnboardingModalOnGroupClass;
  }

  get email(): string {
    return this.getPersonData().email;
  }

  get firstName(): string {
    return this.getPersonData().firstName;
  }

  get oePlacementTestTaken(): boolean {
    return this.getPersonData().oePlacementTestTaken;
  }

  get levelingAction(): LevelingAction | null {
    return this.getPersonData().levelingAction || null;
  }

  get bilingualEnabled(): boolean {
    return this.getPersonData().bilingualEnabled;
  }

  get preferredLang(): Language {
    return this.getPersonData().preferredLang as Language;
  }

  get nativeLang(): Language {
    return this.getPersonData().nativeLang as Language;
  }

  get id(): number {
    return this.getPersonData().id;
  }

  get uuid(): string {
    return this.getPersonData().uuid;
  }

  get contactId(): string {
    return this.getPersonData().contactId;
  }

  get levelId(): number {
    return this.getPersonData().levelId;
  }

  get isLfa(): boolean {
    return this.getPersonData().lfa;
  }

  get isBilingualEnabled(): boolean {
    return this.getPersonData().bilingualEnabled;
  }

  get preferedTeacherType(): TeacherType {
    return this.getPersonData().preferredTeacherType as TeacherType;
  }

  get fallBackLiveClassTeacherType(): BilingualFallbackType {
    return this.getPersonData().fallBackLiveClassTeacherType;
  }

  get isLcOnboardingTaken(): boolean {
    return this.getPersonData().lcOnboardingTaken;
  }

  get isReferralProgramEnabled(): boolean {
    return this.getPersonData().referralProgramEnabled;
  }

  get companyName(): string {
    return this.getPersonData().companyName;
  }

  get companyUuid(): string {
    return this.getPersonData().companyUuid;
  }

  get enforcePlatformTour(): boolean {
    return this.getPersonData().enforcePlatformTour;
  }

  get platformTourEnabled(): boolean {
    return this.getPersonData().platformTourEnabled;
  }

  private get program(): Program {
    return this.getPersonData().program || ({} as Program);
  }

  get hideMenuItems(): string[] {
    return this.program.hideMenuItems || [];
  }

  get iscEnabled(): Program['iscEnabled'] | undefined {
    return this.program && this.program.iscEnabled;
  }

  get certificateEnabled(): Program['certificateEnabled'] | undefined {
    return this.program && this.program.certificateEnabled;
  }

  get testPrepEnabled(): Program['testPrepEnabled'] | undefined {
    return this.program && this.program.testPrepEnabled;
  }

  get productCategory(): Program['productCategory'] | undefined {
    if ('productCategory' in this.program) {
      return this.program.productCategory;
    }
  }

  get lessonType(): Program['lessonType'] | undefined {
    if ('lessonType' in this.program) {
      return this.program.lessonType;
    }
  }

  get lessonEnabled(): Program['lessonEnabled'] {
    return this.program.lessonEnabled;
  }

  get liveClassEnabled(): Program['liveClassEnabled'] {
    return this.program.liveClassEnabled;
  }

  get cohortLiveClassEnabled(): Program['cohortLiveClassEnabled'] {
    return this.program.cohortLiveClassEnabled;
  }

  get changeCohortEnabled(): Program['changeCohortEnabled'] {
    return this.program.changeCohortEnabled;
  }

  get cohortId() {
    return this.oeApp.getPersonData().cohortId;
  }

  get copilotEnabled(): boolean {
    return this.oeApp.getPersonData().copilotEnabled;
  }

  get hasSeenCopilotOnboarding(): boolean {
    return this.oeApp.getPersonData().hasSeenCopilotOnboarding;
  }

  get placementTestEnabled(): boolean {
    return this.program && this.program.placementTestEnabled;
  }

  get showProgressTestBanner(): boolean {
    return this.program && this.program.showProgressTestBanner;
  }

  get tosRequired(): boolean {
    return this.program && this.program.tosRequired;
  }

  get manualLevelSelectionEnabled(): boolean {
    return this.oeApp.getPersonData().manualLevelSelectionEnabled;
  }

  get showLevelSelectionButton(): boolean {
    return this.manualLevelSelectionEnabled && !this.isB2bStudent();
  }

  get unitIndexEnabled(): boolean {
    return !CohortHelpers.isSaoPauloProgramStudent(this.productCategory);
  }

  get disabilityFlag(): boolean {
    return this.getPersonData().disabilityFlag;
  }

  get offlineModeEnabled(): boolean {
    return !!this.program.offlineModeEnabled;
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/oe-app-person.service.ts